import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Spin } from "antd";
import { LikeFilled, LikeOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { showConceptLibraryDesktopTipModal } from "../../space/common/DesktopOnlyTipModal";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppAvatarUrl } from "../../utils/resolveAppMediaUrl";
import { useMainScrollPullRefresh } from "../../utils/useMainScrollPullRefresh";
import { useTabsRowTouchIsolation } from "../../utils/useTabsRowTouchIsolation";
import { useTabsScrollFadeRight } from "../../utils/useTabsScrollFadeRight";
import { ConceptApi } from "../concept/ConceptApi";
import type { ConceptBrief } from "../concept/ConceptType";
import { viewpointFeedLayoutCssVars } from "./Config";
import { ViewpointConceptLinkedBody, ViewpointConceptTagRow } from "./ViewpointConceptContent";
import { ViewpointFeedApi } from "./ViewpointFeedApi";
import type { ViewpointFeedCategoryItem, ViewpointFeedPost } from "./ViewpointFeedTypes";
import "./ViewpointFeedPage.css";
import { ViewpointHotBadge } from "./ViewpointHotBadge";
import { formatViewpointEngagementCount } from "./ViewpointUtil";

const PAGE_SIZE = 8;
const POLL_MS = 5000;

function normalizeFeedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    likeCount: p.likeCount ?? 0,
    collectCount: p.collectCount ?? 0,
  };
}

function ViewpointFeedEngagementActions({
  row,
  onToggleLike,
  onToggleCollect,
}: {
  row: ViewpointFeedPost;
  onToggleLike: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
  onToggleCollect: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  const liked = Boolean(row.likedByMe);
  const collected = Boolean(row.collectedByMe);
  return (
    <div className="lf-vp-moment-actions" aria-label="点赞与收藏">
      <button
        type="button"
        className="lf-vp-moment-action-btn"
        onClick={(e) => void onToggleLike(e, row)}
        aria-label={liked ? "取消点赞" : "点赞"}
      >
        {liked ? (
          <LikeFilled className="lf-vp-moment-action-ic lf-vp-moment-action-ic--on" />
        ) : (
          <LikeOutlined className="lf-vp-moment-action-ic" />
        )}
        <span>{formatViewpointEngagementCount(row.likeCount ?? 0)}</span>
      </button>
      <button
        type="button"
        className="lf-vp-moment-action-btn"
        onClick={(e) => void onToggleCollect(e, row)}
        aria-label={collected ? "取消收藏" : "收藏"}
      >
        {collected ? (
          <StarFilled className="lf-vp-moment-action-ic lf-vp-moment-action-ic--collect-on" />
        ) : (
          <StarOutlined className="lf-vp-moment-action-ic" />
        )}
        <span>{formatViewpointEngagementCount(row.collectCount ?? 0)}</span>
      </button>
    </div>
  );
}

function displayPublishedCompact(v: unknown): string {
  if (v == null || v === "") return "";
  if (v instanceof Date) return dayjs(v).format("M月D日 HH:mm");
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("M月D日 HH:mm") : t;
  }
  return String(v).trim();
}

function nicknameOf(row: ViewpointFeedPost): string {
  const n = row.publisherNickname?.trim();
  if (n) return n;
  return row.publisherLabel?.trim() || "用户";
}

function refreshedAtLabel(t: Date | null): string {
  if (!t) return "—";
  return dayjs(t).format("M月D日 HH:mm");
}

type FeedListFilter = "all" | "liked" | "collected";

function importanceClass(imp: string): string {
  if (imp === "urgent") return "lf-vp-moment-imp lf-vp-moment-imp--urgent";
  if (imp === "important") return "lf-vp-moment-imp lf-vp-moment-imp--important";
  return "lf-vp-moment-imp lf-vp-moment-imp--normal";
}

function importanceLabel(imp: string): string {
  if (imp === "urgent") return "精选";
  if (imp === "important") return "重要";
  return "";
}

function pullRefreshHint(phase: string): string {
  if (phase === "refreshing") return "刷新中…";
  if (phase === "ready") return "松开刷新";
  if (phase === "pulling") return "下拉刷新";
  return "";
}

export interface ViewpointFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  spaceDisplayName: string;
  /** 同空间概念产品 ID，用于正文概念识别 */
  conceptSpaceProductId?: string;
}

const ViewpointFeedPage: React.FC<ViewpointFeedPageProps> = observer(
  ({ spaceId, spaceProductId, conceptSpaceProductId }) => {
    const [items, setItems] = useState<ViewpointFeedPost[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [feedFilter, setFeedFilter] = useState<FeedListFilter>("all");
    const [feedCategories, setFeedCategories] = useState<ViewpointFeedCategoryItem[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [conceptCatalog, setConceptCatalog] = useState<ConceptBrief[]>([]);
    const itemsRef = useRef<ViewpointFeedPost[]>([]);
    itemsRef.current = items;
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const tabsRowRef = useRef<HTMLDivElement | null>(null);
    const tabsScrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      setSelectedCategoryId(null);
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      void (async () => {
        const res = await ViewpointFeedApi.listViewpointCategories({ spaceId, spaceProductId });
        if (typeof res === "string") {
          setFeedCategories([]);
          return;
        }
        setFeedCategories(Array.isArray(res.list) ? res.list : []);
      })();
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      const pid = conceptSpaceProductId?.trim();
      if (!pid) {
        setConceptCatalog([]);
        return;
      }
      void (async () => {
        try {
          const list = await ConceptApi.listConcepts(pid);
          setConceptCatalog(Array.isArray(list) ? list : []);
        } catch {
          setConceptCatalog([]);
        }
      })();
    }, [conceptSpaceProductId]);

    const reloadFirstPage = useCallback(async () => {
      setLoading(true);
      try {
        const res = await ViewpointFeedApi.getOldViewpoints({
          spaceId,
          spaceProductId,
          limit: PAGE_SIZE,
          ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
        });
        if (typeof res === "string") {
          setItems([]);
          setHasMoreOlder(false);
          return;
        }
        setHasMoreOlder(res.hasMore);
        setItems(res.list.map(normalizeFeedPost));
        setRefreshedAt(new Date());
      } finally {
        setLoading(false);
      }
    }, [spaceId, spaceProductId, selectedCategoryId]);

    const { pullDistance, phase: pullPhase } = useMainScrollPullRefresh(reloadFirstPage, true);
    useTabsRowTouchIsolation(tabsRowRef, tabsScrollRef);

    const displayItems = useMemo(() => {
      if (feedFilter === "liked") {
        return items.filter((x) => Boolean(x.likedByMe));
      }
      if (feedFilter === "collected") {
        return items.filter((x) => Boolean(x.collectedByMe));
      }
      return items;
    }, [items, feedFilter]);

    useEffect(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loading || loadingMore) return;
          if (!hasMoreOlder) return;
          const last = itemsRef.current[itemsRef.current.length - 1];
          if (!last) return;
          setLoadingMore(true);
          void (async () => {
            try {
              const res = await ViewpointFeedApi.getOldViewpoints({
                spaceId,
                spaceProductId,
                limit: PAGE_SIZE,
                cursorPublishedAt: last.publishedAt,
                cursorId: last.id,
                ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
              });
              if (typeof res === "string") {
                return;
              }
              setHasMoreOlder(res.hasMore);
              setItems((prev) => [...prev, ...res.list.map(normalizeFeedPost)]);
            } finally {
              setLoadingMore(false);
            }
          })();
        },
        { root: null, rootMargin: "120px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [loading, loadingMore, hasMoreOlder, spaceId, spaceProductId, selectedCategoryId, items.length]);

    useEffect(() => {
      const id = window.setInterval(() => {
        const top = itemsRef.current[0];
        if (!top) return;
        void (async () => {
          const res = await ViewpointFeedApi.getLatestViewpoints({
            spaceId,
            spaceProductId,
            limit: PAGE_SIZE,
            cursorPublishedAt: top.publishedAt,
            cursorId: top.id,
            ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
          });
          if (typeof res === "string" || !res.list.length) return;
          const incoming = res.list.map(normalizeFeedPost);
          const seen = new Set(itemsRef.current.map((p) => p.id));
          const merged = incoming.filter((p) => !seen.has(p.id));
          if (!merged.length) return;
          setItems((prev) => [...merged, ...prev]);
          setRefreshedAt(new Date());
        })();
      }, POLL_MS);
      return () => window.clearInterval(id);
    }, [spaceId, spaceProductId, selectedCategoryId]);

    const patchEngagement = useCallback((id: string, liked: boolean, collected: boolean) => {
      setItems((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          let likeCount = r.likeCount ?? 0;
          let collectCount = r.collectCount ?? 0;
          const wasLiked = Boolean(r.likedByMe);
          const wasColl = Boolean(r.collectedByMe);
          if (liked !== wasLiked) {
            likeCount += liked ? 1 : -1;
          }
          if (collected !== wasColl) {
            collectCount += collected ? 1 : -1;
          }
          return {
            ...r,
            likedByMe: liked,
            collectedByMe: collected,
            likeCount: Math.max(0, likeCount),
            collectCount: Math.max(0, collectCount),
          };
        })
      );
    }, []);

    const onToggleLike = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleLike({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") return;
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onToggleCollect = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleCollect({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") return;
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onConceptClick = useCallback((_concept: ConceptBrief) => {
      showConceptLibraryDesktopTipModal();
    }, []);

    const showEnd = !loading && items.length > 0 && !hasMoreOlder;
    const showFilteredEmpty = !loading && items.length > 0 && displayItems.length === 0;

    const onPickFeedCategory = useCallback(
      (categoryId: string) => {
        const id = categoryId.trim();
        if (!id) return;
        const nextId = selectedCategoryId === id ? null : id;
        setSelectedCategoryId(nextId);
        if (nextId !== null) {
          setFeedFilter("all");
        }
      },
      [selectedCategoryId]
    );

    const onPickFeedFilter = useCallback((key: FeedListFilter) => {
      setFeedFilter(key);
      if (key === "all" || key === "liked" || key === "collected") {
        setSelectedCategoryId(null);
      }
    }, []);

    const validFeedCategories = useMemo(
      () =>
        feedCategories.filter((c) => {
          const nm = c.name?.trim();
          const sid = c.id?.trim();
          return Boolean(nm && sid);
        }),
      [feedCategories]
    );

    const tabsScrollFadeRight = useTabsScrollFadeRight(
      tabsScrollRef,
      validFeedCategories.length
    );

    const ptrVisible = pullDistance > 0 || pullPhase === "refreshing";

    return (
      <div
        className="lf-vp-feed lf-vp-feed--h5 lf-vp-feed--moments"
        style={viewpointFeedLayoutCssVars()}
      >
        <div className="lf-vp-feed__inner">
          <div className="lf-vp-feed__toolbar" aria-label="列表筛选与刷新">
            <div
              ref={tabsRowRef}
              className="lf-vp-feed__tabs-row"
              role="tablist"
              aria-label="观点分类"
            >
              <button
                type="button"
                role="tab"
                aria-selected={feedFilter === "all" && !selectedCategoryId}
                className={
                  feedFilter === "all" && !selectedCategoryId
                    ? "lf-vp-feed__filter lf-vp-feed__filter--on lf-vp-feed__filter--pinned"
                    : "lf-vp-feed__filter lf-vp-feed__filter--pinned"
                }
                onClick={() => onPickFeedFilter("all")}
              >
                全部
              </button>
              <div
                className={
                  tabsScrollFadeRight
                    ? "lf-vp-feed__tabs-scroll-wrap lf-vp-feed__tabs-scroll-wrap--fade-right"
                    : "lf-vp-feed__tabs-scroll-wrap"
                }
              >
                <div ref={tabsScrollRef} className="lf-vp-feed__tabs-scroll">
                <div className="lf-vp-feed__tabs-scroll-inner">
                  {(
                    [
                      ["liked", "我点赞的"],
                      ["collected", "我收藏的"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={feedFilter === key}
                      className={
                        feedFilter === key
                          ? "lf-vp-feed__filter lf-vp-feed__filter--on"
                          : "lf-vp-feed__filter"
                      }
                      onClick={() => onPickFeedFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                  {validFeedCategories.map((c) => {
                    const nm = c.name!.trim();
                    const on = selectedCategoryId === c.id.trim();
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        className={
                          on ? "lf-vp-feed__filter lf-vp-feed__filter--on" : "lf-vp-feed__filter"
                        }
                        onClick={() => onPickFeedCategory(c.id)}
                      >
                        {nm}
                      </button>
                    );
                  })}
                </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="lf-vp-feed__refresh"
              onClick={() => void reloadFirstPage()}
              disabled={loading}
              aria-label="立即刷新列表"
            >
              更新于 {refreshedAtLabel(refreshedAt)}
            </button>
          </div>

          <div className="lf-vp-feed__body">
            <div
              className={
                ptrVisible
                  ? "lf-vp-feed__ptr lf-vp-feed__ptr--visible"
                  : "lf-vp-feed__ptr"
              }
              style={{ height: ptrVisible ? `${pullDistance}px` : 0 }}
              aria-hidden={!ptrVisible}
            >
              <span className="lf-vp-feed__ptr-text">
                {pullPhase === "refreshing" ? (
                  <>
                    <Spin size="small" /> {pullRefreshHint(pullPhase)}
                  </>
                ) : (
                  pullRefreshHint(pullPhase)
                )}
              </span>
            </div>

            <Spin spinning={loading && pullPhase !== "refreshing"}>
              <div className="lf-vp-feed__list">
              {showFilteredEmpty ? (
                <div className="lf-vp-feed__empty-filter">当前筛选下暂无内容</div>
              ) : null}
              {displayItems.map((row) => {
                const nick = nicknameOf(row);
                const titleText =
                  row.titleMode && row.title?.trim() ? row.title.trim() : null;
                const categoryName = row.categoryName?.trim() || "";
                const categoryId = row.categoryId?.trim() || "";
                const showImportance =
                  row.importance === "important" || row.importance === "urgent";
                const imageUrls = row.imageUrls?.slice(0, 9) ?? [];
                const imageGridClass =
                  imageUrls.length === 1
                    ? "lf-vp-moment-images lf-vp-moment-images--single"
                    : "lf-vp-moment-images";

                return (
                  <article key={row.id} className="lf-vp-moment">
                    <EasyAvatar
                      shape="square"
                      squareRadius={4}
                      size={40}
                      url={resolveAppAvatarUrl(row.publisherAvatarUrl) || undefined}
                      word={avatarDisplayWord(nick, row.publisherLabel)}
                      alt=""
                      className="lf-vp-moment-avatar"
                    />
                    <div className="lf-vp-moment-main">
                      <div className="lf-vp-moment-head">
                        <span className="lf-vp-moment-nick">{nick}</span>
                        {categoryName && categoryId ? (
                          <button
                            type="button"
                            className="lf-vp-moment-category"
                            onClick={() => onPickFeedCategory(categoryId)}
                          >
                            {categoryName}
                          </button>
                        ) : null}
                      </div>
                      {titleText ? (
                        <div className="lf-vp-moment-title">{titleText}</div>
                      ) : null}
                      <ViewpointConceptLinkedBody
                        content={row.content}
                        catalog={conceptCatalog}
                        onConceptClick={onConceptClick}
                        importancePrefix={
                          showImportance ? (
                            <span className={importanceClass(row.importance)}>
                              {importanceLabel(row.importance)}
                            </span>
                          ) : null
                        }
                        hotBadge={<ViewpointHotBadge likeCount={row.likeCount ?? 0} />}
                      />
                      {imageUrls.length > 0 ? (
                        <div className={imageGridClass}>
                          <Image.PreviewGroup preview={{ destroyOnClose: true }}>
                            {imageUrls.map((u, idx) => (
                              <div key={`${idx}-${u}`} className="lf-vp-moment-image-cell">
                                <Image src={u} alt="" className="lf-vp-moment-image" />
                              </div>
                            ))}
                          </Image.PreviewGroup>
                        </div>
                      ) : null}
                      <ViewpointConceptTagRow
                        content={row.content}
                        catalog={conceptCatalog}
                        onConceptClick={onConceptClick}
                      />
                      <div className="lf-vp-moment-footer">
                        <span className="lf-vp-moment-time">
                          {displayPublishedCompact(row.publishedAt)}
                        </span>
                        <ViewpointFeedEngagementActions
                          row={row}
                          onToggleLike={onToggleLike}
                          onToggleCollect={onToggleCollect}
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
              <div ref={sentinelRef} className="lf-vp-feed__sentinel" aria-hidden />
              {loadingMore ? (
                <div className="lf-vp-feed__loading-more">
                  <Spin size="small" /> 加载中…
                </div>
              ) : null}
              {showEnd ? <div className="lf-vp-feed__end">已经到底啦</div> : null}
            </div>
          </Spin>
          </div>
        </div>
      </div>
    );
  }
);

export default ViewpointFeedPage;
